import axios from "axios";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useNavigate,
  useParams,
} from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";

const RegistrarProgramaInstructores = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const isEdit = useMemo(
    () => Boolean(id),
    [id]
  );

  const API_PROGRAMA_INSTRUCTORES =
    "/api/programainstructores";

  const API_PROGRAMAS =
    "/api/programas";

  const API_INSTRUCTORES =
    "/api/instructores";

  const [loading, setLoading] =
    useState(false);

  const [
    cargandoInicial,
    setCargandoInicial,
  ] = useState(false);

  const [programas, setProgramas] =
    useState([]);

  const [
    instructores,
    setInstructores,
  ] = useState([]);

  const [
    programaObjectId,
    setProgramaObjectId,
  ] = useState("");

  const [
    instructorObjectId,
    setInstructorObjectId,
  ] = useState("");

  const [
    programaSeleccionado,
    setProgramaSeleccionado,
  ] = useState(null);

  const [
    instructorSeleccionado,
    setInstructorSeleccionado,
  ] = useState(null);

  const [
    rolInstructor,
    setRolInstructor,
  ] = useState("LIDER");

  const [
    estadoAsignacion,
    setEstadoAsignacion,
  ] = useState("ACTIVO");

  const [
    observacionAsignacion,
    setObservacionAsignacion,
  ] = useState("");

  // Configuración de autenticación
  const authConfig = useCallback(() => {
    const token =
      localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, []);

  // Controlar sesión expirada
  const manejarErrorSesion = useCallback(
    (error) => {
      if (
        error?.response?.status === 401
      ) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem(
            "token"
          );

          navigate("/login");
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  // Estilos de react-select
  const selectStyles = useMemo(
    () => ({
      container: (base) => ({
        ...base,
        width: "100%",
      }),

      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),

      menu: (base) => ({
        ...base,
        zIndex: 9999,
      }),

      control: (base, state) => ({
        ...base,
        minHeight: 38,

        borderColor: state.isFocused
          ? "#86b7fe"
          : base.borderColor,

        boxShadow: state.isFocused
          ? "0 0 0 .25rem rgba(13,110,253,.15)"
          : "none",
      }),
    }),
    []
  );

  /*
   * Construir el texto mostrado
   * en el selector de programas.
   *
   * El campo correcto del código
   * es codPrograma.
   */
  const obtenerNombrePrograma =
    useCallback((programa = {}) => {
      const codigo =
        programa.codPrograma || "";

      const nombre =
        programa.nombrePrograma ||
        programa.nombreProgramaFormacion ||
        programa.denominacionPrograma ||
        programa.nombre ||
        "Sin nombre";

      return codigo
        ? `${codigo} - ${nombre}`
        : nombre;
    }, []);

  // Construir el nombre completo del instructor
  const obtenerNombreInstructor =
    useCallback((instructor = {}) => {
      const nombreCompleto = [
        instructor.nombre1Instructor,
        instructor.nombre2Instructor,
        instructor.apellido1Instructor,
        instructor.apellido2Instructor,
      ]
        .filter(Boolean)
        .join(" ");

      const documento =
        instructor.documentoInstructor ||
        "Sin documento";

      return `${
        nombreCompleto || "Sin nombre"
      } - ${documento}`;
    }, []);

  // Opciones del selector de programas
  const opcionesProgramas =
    useMemo(() => {
      return programas.map(
        (programa) => ({
          value: programa._id,

          label:
            obtenerNombrePrograma(
              programa
            ),
        })
      );
    }, [
      programas,
      obtenerNombrePrograma,
    ]);

  // Opciones del selector de instructores
  const opcionesInstructores =
    useMemo(() => {
      return instructores.map(
        (instructor) => ({
          value: instructor._id,

          label:
            obtenerNombreInstructor(
              instructor
            ),
        })
      );
    }, [
      instructores,
      obtenerNombreInstructor,
    ]);

  // Limpiar formulario
  const cleanData = useCallback(() => {
    setProgramaObjectId("");
    setInstructorObjectId("");

    setProgramaSeleccionado(null);
    setInstructorSeleccionado(null);

    setRolInstructor("LIDER");
    setEstadoAsignacion("ACTIVO");

    setObservacionAsignacion("");

    localStorage.removeItem(
      "programaInstructorEdit"
    );
  }, []);

  // Consultar programas de formación
  const getProgramas =
    useCallback(async () => {
      const { data } =
        await axios.get(
          `${API_PROGRAMAS}/listall`,
          authConfig()
        );

      const listaProgramas =
        data?.programas ||
        data?.programasFormacion ||
        data?.programaformacion ||
        [];

      setProgramas(
        Array.isArray(listaProgramas)
          ? listaProgramas
          : []
      );
    }, [authConfig]);

  // Consultar instructores
  const getInstructores =
    useCallback(async () => {
      const { data } =
        await axios.get(
          `${API_INSTRUCTORES}/listall`,
          authConfig()
        );

      setInstructores(
        Array.isArray(
          data?.instructores
        )
          ? data.instructores
          : []
      );
    }, [authConfig]);

  // Cargar programas e instructores
  const cargarDatosBase =
    useCallback(async () => {
      try {
        setCargandoInicial(true);

        await Promise.all([
          getProgramas(),
          getInstructores(),
        ]);
      } catch (error) {
        console.error(
          "Error cargando programas e instructores:",
          error
        );

        if (
          manejarErrorSesion(error)
        ) {
          return;
        }

        Swal.fire(
          "Error",
          "No se pudieron cargar los programas e instructores.",
          "error"
        );
      } finally {
        setCargandoInicial(false);
      }
    }, [
      getProgramas,
      getInstructores,
      manejarErrorSesion,
    ]);

  // Cargar información desde localStorage
  const cargarDesdeLocalStorage =
    useCallback(() => {
      const asignacionStorage =
        localStorage.getItem(
          "programaInstructorEdit"
        );

      if (
        !id ||
        !asignacionStorage
      ) {
        return false;
      }

      try {
        const asignacion =
          JSON.parse(
            asignacionStorage
          );

        const programaId =
          typeof asignacion.programaObjectId ===
          "object"
            ? asignacion
                .programaObjectId?._id
            : asignacion.programaObjectId;

        const instructorId =
          typeof asignacion.instructorObjectId ===
          "object"
            ? asignacion
                .instructorObjectId?._id
            : asignacion.instructorObjectId;

        setProgramaObjectId(
          programaId || ""
        );

        setInstructorObjectId(
          instructorId || ""
        );

        setProgramaSeleccionado(
          programaId
            ? {
                value: programaId,

                label:
                  asignacion.programaLabel ||
                  "Programa seleccionado",
              }
            : null
        );

        setInstructorSeleccionado(
          instructorId
            ? {
                value: instructorId,

                label:
                  asignacion.instructorLabel ||
                  "Instructor seleccionado",
              }
            : null
        );

        setRolInstructor(
          asignacion.rolInstructor ||
            "LIDER"
        );

        setEstadoAsignacion(
          asignacion.estadoAsignacion ||
            "ACTIVO"
        );

        setObservacionAsignacion(
          asignacion
            .observacionAsignacion ||
            ""
        );

        return true;
      } catch (error) {
        console.error(
          "Error leyendo programaInstructorEdit:",
          error
        );

        localStorage.removeItem(
          "programaInstructorEdit"
        );

        return false;
      }
    }, [id]);

  // Consultar una asignación por ID
  const getAsignacionById =
    useCallback(async () => {
      if (!id) return;

      try {
        setLoading(true);

        const { data } =
          await axios.get(
            `${API_PROGRAMA_INSTRUCTORES}/listid/${id}`,
            authConfig()
          );

        if (
          !data?.ok ||
          !data?.asignacion
        ) {
          Swal.fire(
            "Error",
            "No se encontró la asignación.",
            "error"
          );

          return;
        }

        const asignacion =
          data.asignacion;

        const programa =
          asignacion.programaObjectId ||
          {};

        const instructor =
          asignacion.instructorObjectId ||
          {};

        const programaId =
          typeof programa === "object"
            ? programa._id
            : programa;

        const instructorId =
          typeof instructor === "object"
            ? instructor._id
            : instructor;

        setProgramaObjectId(
          programaId || ""
        );

        setInstructorObjectId(
          instructorId || ""
        );

        setProgramaSeleccionado(
          programaId
            ? {
                value: programaId,

                label:
                  typeof programa ===
                  "object"
                    ? obtenerNombrePrograma(
                        programa
                      )
                    : "Programa seleccionado",
              }
            : null
        );

        setInstructorSeleccionado(
          instructorId
            ? {
                value: instructorId,

                label:
                  typeof instructor ===
                  "object"
                    ? obtenerNombreInstructor(
                        instructor
                      )
                    : "Instructor seleccionado",
              }
            : null
        );

        setRolInstructor(
          asignacion.rolInstructor ||
            "LIDER"
        );

        setEstadoAsignacion(
          asignacion.estadoAsignacion ||
            "ACTIVO"
        );

        setObservacionAsignacion(
          asignacion
            .observacionAsignacion ||
            ""
        );
      } catch (error) {
        console.error(
          "Error cargando la asignación:",
          error
        );

        if (
          manejarErrorSesion(error)
        ) {
          return;
        }

        Swal.fire(
          "Error",
          error.response?.data
            ?.message ||
            "No fue posible cargar la asignación.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    }, [
      id,
      authConfig,
      manejarErrorSesion,
      obtenerNombrePrograma,
      obtenerNombreInstructor,
    ]);

  // Cargar datos base
  useEffect(() => {
    cargarDatosBase();
  }, [cargarDatosBase]);

  // Cargar asignación cuando se edita
  useEffect(() => {
    if (isEdit) {
      const cargadoLocal =
        cargarDesdeLocalStorage();

      if (!cargadoLocal) {
        getAsignacionById();
      }
    }
  }, [
    isEdit,
    cargarDesdeLocalStorage,
    getAsignacionById,
  ]);

  // Cambiar programa seleccionado
  const handleProgramaChange = (
    selectedOption
  ) => {
    setProgramaSeleccionado(
      selectedOption || null
    );

    setProgramaObjectId(
      selectedOption?.value || ""
    );
  };

  // Cambiar instructor seleccionado
  const handleInstructorChange = (
    selectedOption
  ) => {
    setInstructorSeleccionado(
      selectedOption || null
    );

    setInstructorObjectId(
      selectedOption?.value || ""
    );
  };

  // Registrar una nueva asignación
  const saveAsignacion =
    async () => {
      const nuevaAsignacion = {
        programaObjectId,
        instructorObjectId,
        rolInstructor,
        estadoAsignacion,

        observacionAsignacion:
          observacionAsignacion.trim(),
      };

      const { data } =
        await axios.post(
          `${API_PROGRAMA_INSTRUCTORES}/add`,
          nuevaAsignacion,
          authConfig()
        );

      if (!data?.ok) {
        Swal.fire(
          "Error",
          data?.message ||
            "No se pudo registrar la asignación.",
          "error"
        );

        return;
      }

      await Swal.fire({
        icon: "success",

        title:
          "Asignación registrada con éxito",

        text: "El instructor fue asociado al programa de formación.",

        showConfirmButton: false,
        timer: 1700,
      });

      cleanData();

      navigate(
        "/programainstructores"
      );
    };

  // Actualizar una asignación
  const updateAsignacion =
    async () => {
      const asignacionUpdate = {
        programaObjectId,
        instructorObjectId,
        rolInstructor,
        estadoAsignacion,

        observacionAsignacion:
          observacionAsignacion.trim(),
      };

      const { data } =
        await axios.put(
          `${API_PROGRAMA_INSTRUCTORES}/update/${id}`,
          asignacionUpdate,
          authConfig()
        );

      if (!data?.ok) {
        Swal.fire(
          "Error",
          data?.message ||
            "No se pudo actualizar la asignación.",
          "error"
        );

        return;
      }

      await Swal.fire({
        icon: "success",

        title:
          "Asignación actualizada con éxito",

        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();

      navigate(
        "/programainstructores"
      );
    };

  // Enviar formulario
  const actions = async (e) => {
    e.preventDefault();

    if (
      loading ||
      cargandoInicial
    ) {
      return;
    }

    if (!programaObjectId) {
      Swal.fire(
        "Atención",
        "Debe seleccionar un programa de formación.",
        "warning"
      );

      return;
    }

    if (!instructorObjectId) {
      Swal.fire(
        "Atención",
        "Debe seleccionar un instructor.",
        "warning"
      );

      return;
    }

    if (
      !["LIDER", "APOYO"].includes(
        rolInstructor
      )
    ) {
      Swal.fire(
        "Atención",
        "El rol del instructor no es válido.",
        "warning"
      );

      return;
    }

    try {
      setLoading(true);

      if (isEdit) {
        await updateAsignacion();
      } else {
        await saveAsignacion();
      }
    } catch (error) {
      console.error(
        "Error guardando la asignación:",
        error
      );

      if (
        manejarErrorSesion(error)
      ) {
        return;
      }

      Swal.fire(
        "Error",
        error.response?.data
          ?.message ||
          "Hubo un problema con la solicitud.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Cancelar registro o edición
  const handleCancel = () => {
    cleanData();

    navigate(
      "/programainstructores"
    );
  };

  const cargando =
    loading || cargandoInicial;

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">

          {/* Encabezado */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">

              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit
                    ? "Editar asignación de instructor"
                    : "Asignar instructor a programa"}
                </h5>

                <small className="text-secondary">
                  Relacione un
                  instructor con un
                  programa de
                  formación.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() =>
                    navigate(
                      "/programainstructores"
                    )
                  }
                  disabled={cargando}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>
              </div>

            </div>
          </div>

          {/* Contenido */}
          <div className="p-3 p-md-4">

            {cargando && (
              <div className="text-center text-secondary py-3">
                <div
                  className="spinner-border"
                  role="status"
                />

                <div className="mt-2">
                  {cargandoInicial
                    ? "Cargando datos base…"
                    : isEdit
                    ? "Cargando o actualizando asignación…"
                    : "Procesando…"}
                </div>
              </div>
            )}

            <form
              id="form-programa-instructores"
              onSubmit={actions}
              className="needs-validation"
              noValidate
            >
              <div className="row g-3">

                {/* Programa */}
                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">
                    Programa de formación
                  </label>

                  <Select
                    options={
                      opcionesProgramas
                    }
                    value={
                      programaSeleccionado
                    }
                    onChange={
                      handleProgramaChange
                    }
                    placeholder="Seleccione un programa"
                    noOptionsMessage={() =>
                      "No hay programas disponibles"
                    }
                    isClearable
                    isSearchable
                    styles={
                      selectStyles
                    }
                    menuPortalTarget={
                      document.body
                    }
                    menuPosition="fixed"
                    isDisabled={
                      cargando
                    }
                  />
                </div>

                {/* Instructor */}
                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">
                    Instructor
                  </label>

                  <Select
                    options={
                      opcionesInstructores
                    }
                    value={
                      instructorSeleccionado
                    }
                    onChange={
                      handleInstructorChange
                    }
                    placeholder="Seleccione un instructor"
                    noOptionsMessage={() =>
                      "No hay instructores disponibles"
                    }
                    isClearable
                    isSearchable
                    styles={
                      selectStyles
                    }
                    menuPortalTarget={
                      document.body
                    }
                    menuPosition="fixed"
                    isDisabled={
                      cargando
                    }
                  />
                </div>

                {/* Rol */}
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">
                    Rol del instructor
                  </label>

                  <select
                    className="form-select"
                    value={
                      rolInstructor
                    }
                    onChange={(e) =>
                      setRolInstructor(
                        e.target.value
                      )
                    }
                    disabled={
                      cargando
                    }
                  >
                    <option value="LIDER">
                      LÍDER
                    </option>

                    <option value="APOYO">
                      APOYO
                    </option>
                  </select>
                </div>

                {/* Estado */}
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">
                    Estado
                  </label>

                  <select
                    className="form-select"
                    value={
                      estadoAsignacion
                    }
                    onChange={(e) =>
                      setEstadoAsignacion(
                        e.target.value
                      )
                    }
                    disabled={
                      cargando
                    }
                  >
                    <option value="ACTIVO">
                      ACTIVO
                    </option>

                    <option value="INACTIVO">
                      INACTIVO
                    </option>
                  </select>
                </div>

                {/* Observación */}
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">
                    Observación
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    placeholder="Observación opcional"
                    value={
                      observacionAsignacion
                    }
                    onChange={(e) =>
                      setObservacionAsignacion(
                        e.target.value
                      )
                    }
                    maxLength={500}
                    disabled={
                      cargando
                    }
                  />
                </div>

                {/* Botones */}
                <div className="col-12 pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">

                    <button
                      type="button"
                      className="btn btn-outline-dark"
                      onClick={
                        cleanData
                      }
                      disabled={
                        cargando
                      }
                    >
                      <i className="fa-solid fa-eraser me-2"></i>
                      Limpiar
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={
                        handleCancel
                      }
                      disabled={
                        cargando
                      }
                    >
                      <i className="fa-solid fa-xmark me-2"></i>
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      form="form-programa-instructores"
                      className="btn btn-dark"
                      disabled={
                        cargando
                      }
                    >
                      <i
                        className={`fa-solid ${
                          cargando
                            ? "fa-spinner fa-spin"
                            : "fa-floppy-disk"
                        } me-2`}
                      ></i>

                      {cargando
                        ? isEdit
                          ? "Actualizando…"
                          : "Guardando…"
                        : isEdit
                        ? "Actualizar"
                        : "Guardar"}
                    </button>

                  </div>
                </div>

              </div>
            </form>

          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarProgramaInstructores;